<script setup lang="ts">
import type { DropdownItem } from '#ui/types'
import type { ReferenceCategory } from '~/types/api'

const props = defineProps<{
  selectedCategory?: ReferenceCategory
}>()

const { t, te } = useI18n()
const { trimDescription, addSuffix } = useMeta()

useSeoMeta({
  title:
    addSuffix(props.selectedCategory?.title) ??
    t('pages.references.meta.title'),
  description: te('pages.references.meta.description')
    ? t('pages.references.meta.description')
    : trimDescription(t('pages.references.description')),
})

const router = useRouter()
const localePath = useLocalePath()

const { data: referenceCategories } = await useAsyncData(() =>
  createReferencesRepository().getCategories()
)

const allCategoriesLangPath = 'base.references.allCategories'
const dropdownItems = computed(() => {
  const items = [] as DropdownItem[]
  if (props.selectedCategory !== undefined) {
    items.push({
      label: t(allCategoriesLangPath),
      click: getSelectCategoryHandler(),
    })
  }
  referenceCategories.value?.items.forEach((category: ReferenceCategory) => {
    if (
      category.translationOfNid !== props.selectedCategory?.translationOfNid
    ) {
      items.push({
        label: category.title,
        click: getSelectCategoryHandler(category),
      } as DropdownItem)
    }
  })
  return items
})

const dropdownLabel = computed(() => {
  if (props.selectedCategory && props.selectedCategory.title) {
    return props.selectedCategory.title
  }
  return t(allCategoriesLangPath)
})

function getSelectCategoryHandler(referenceCategory?: ReferenceCategory) {
  return () => {
    if (referenceCategory === undefined) {
      router.push(
        localePath({
          name: 'references',
        })
      )
      return
    }
    router.push(
      localePath({
        name: 'references-categories-nid',
        params: { nid: referenceCategory.nid },
      })
    )
  }
}
</script>

<template>
  <UiBanner
    :title="t('pages.references.label')"
    :description="selectedCategory?.title ?? t('pages.references.description')"
    :overrides="[
      undefined,
      undefined,
      false,
      {
        label: selectedCategory?.title,
      },
    ]"
  />
  <div class="container">
    <UDropdown
      :items="[dropdownItems]"
      :popper="{ placement: 'bottom-start' }"
      :ui="{
        base: 'dropdown-nudged',
        ring: 'ring-1 ring-black',
        rounded: '',
      }"
    >
      <button class="bg-black px-3 py-1 text-white">
        <div class="flex justify-between">
          <div>
            {{ dropdownLabel }}
          </div>
          <Icon class="ml-16 mt-1" name="fa6-solid:angle-down" />
        </div>
      </button>
    </UDropdown>
  </div>
  <ReferencesList :selected-category-id="selectedCategory?.translationOfNid" />
  <NewsSubscribe class="bg-neutral-50" />
</template>

<style>
.dropdown-nudged {
  margin-top: -7px;
  margin-left: 1px;
  width: 235.5px;
}
</style>
